import { FC, useEffect, createContext, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as _ from "lodash";

import { Optional, isDispatched, isPending } from "@ctra/utils";
import {
  Enterprise,
  EnterpriseAppState,
  FarmEntity,
  FavouriteChartEntity,
  FavouritesList,
  Favourites
} from "@ctra/api";

interface FavouritesProviderProps {
  farmID: Optional<FarmEntity["id"]>;
}

type ContextType = {
  favourites: FavouritesList;
  addToFavourites: (chart: FavouriteChartEntity) => void;
  removeFromFavourites: (chart: FavouriteChartEntity) => void;
  meta: {
    isAdding: boolean;
  };
};

/**
 * Default Favourites Context
 */
const DefaultContext = createContext<ContextType>({
  favourites: {},
  addToFavourites: _.noop,
  removeFromFavourites: _.noop,
  meta: {
    isAdding: false
  }
});

/**
 * Fetch favourites for the given farm or the dashboard
 * @constructor
 */
export const _FavouritesProvider: FC<FavouritesProviderProps> = ({ farmID, children }) => {
  const dispatch = useDispatch();

  /**
   * Either get favourites for a farm or all farms i.e. main
   */
  const pageKey = _.toString(farmID) || "main";
  /**
   * Attempt to get the favourites
   */
  const favourites = useSelector<EnterpriseAppState, FavouritesList>((state) =>
    Enterprise.entities.getFavourites(state, { pageKey })
  );

  /**
   * Tell whether the fetching action has been dispatched
   */
  const dispatched = useSelector<EnterpriseAppState, boolean>((state) =>
    isDispatched(state, Favourites.types.FETCH_FAVOURITES)
  );

  /**
   * Tell whether a chart is being added
   */
  const isAdding = useSelector<EnterpriseAppState, boolean>((state) =>
    isPending(state, Favourites.types.ADD_TO_FAVOURITES)
  );

  useEffect(() => {
    if (!dispatched) {
      dispatch(Favourites.actions.fetchFavourites.start());
    }
  }, [dispatch, dispatched]);

  const addToFavourites = (chart: FavouriteChartEntity) => {
    /**
     * Send entire list to BE in a denormalized form
     */
    const denormalized = _.map(
      _.values({ ...favourites, chart }),
      ({ dataDescriptorID, view, dateRange, displayFilters, dataFilters }) => ({
        descriptorId: dataDescriptorID,
        chartType: view,
        dateRange,
        displayFilters,
        dataFilters
      })
    );

    dispatch(Favourites.actions.addToFavourites.start(denormalized, chart, pageKey));
  };

  const removeFromFavourites = (chart: FavouriteChartEntity) => {
    const favouritesList = _.omit(favourites, [chart.dataDescriptorID]);

    const denormalized = _.map(
      _.values(favouritesList),
      ({ dataDescriptorID, view, dateRange, displayFilters, dataFilters }) => ({
        descriptorId: dataDescriptorID,
        chartType: _.capitalize(view),
        dateRange,
        displayFilters,
        dataFilters
      })
    );
    //@ts-ignore
    dispatch(Favourites.actions.removeFromFavourites.start(denormalized, chart, pageKey));
  };

  return (
    <DefaultContext.Provider
      value={{ favourites, addToFavourites, removeFromFavourites, meta: { isAdding } }}
    >
      {children}
    </DefaultContext.Provider>
  );
};

export const FavouritesContext = {
  Provider: _FavouritesProvider,
  Consumer: DefaultContext.Consumer
};

/**
 * Hook for Favourites context
 * @returns
 */
export const useFavourites = (): ContextType => useContext<ContextType>(DefaultContext);
